import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Camera, CheckCircle, AlertTriangle, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import './ReportAdSheet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  // Availability issues (yellow theme)
  { key: 'sold', group: 'availability', i18nKey: 'report.reason_sold', theme: 'yellow' },
  { key: 'not_responding', group: 'availability', i18nKey: 'report.reason_not_responding', theme: 'yellow' },
  { key: 'wrong_info', group: 'availability', i18nKey: 'report.reason_wrong_info', theme: 'yellow' },
  // Policy issues (red theme)
  { key: 'scam', group: 'policy', i18nKey: 'report.reason_scam', theme: 'red' },
  { key: 'prohibited', group: 'policy', i18nKey: 'report.reason_prohibited', theme: 'red' },
  { key: 'spam', group: 'policy', i18nKey: 'report.reason_spam', theme: 'red' },
  { key: 'offensive', group: 'policy', i18nKey: 'report.reason_offensive', theme: 'red' },
  { key: 'other', group: 'policy', i18nKey: 'report.reason_other', theme: 'red' },
];

const MAX_EVIDENCE_FILES = 2;

export default function ReportAdSheet({ open, onClose, adId, onReported }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  
  const [mounted, setMounted] = useState(false);
  
  // Form state
  const [step, setStep] = useState('form'); // 'form' | 'confirm' | 'success'
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  
  // Evidence photos
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [evidenceError, setEvidenceError] = useState('');
  
  // Loading states
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const availabilityReasons = useMemo(() => REASONS.filter(r => r.group === 'availability'), []);
  const policyReasons = useMemo(() => REASONS.filter(r => r.group === 'policy'), []);

  // Handle mount/unmount animation
  useEffect(() => {
    if (open) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      const timer = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Reset all state and close
  const resetAll = () => {
    setStep('form');
    setSelected(null);
    setDetails('');
    setAcknowledged(false);
    setFiles([]);
    // Cleanup preview URLs
    previews.forEach(url => URL.revokeObjectURL(url));
    setPreviews([]);
    setEvidenceError('');
    setUploading(false);
    setSubmitting(false);
    onClose();
  };

  // Handle evidence photo selection — APPEND (do not overwrite), cap at MAX.
  // If the user picks a 3rd image, keep the first two and show a localized
  // validation message. Same-file re-pick is allowed (input value cleared).
  const handlePickEvidence = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow picking the same file again later
    if (!picked.length) return;

    const remaining = MAX_EVIDENCE_FILES - files.length;
    if (remaining <= 0) {
      setEvidenceError(t('report.evidence_max_reached'));
      return;
    }

    const accepted = picked.slice(0, remaining);
    const overflow = picked.length > remaining;
    const urls = accepted.map(f => URL.createObjectURL(f));

    // APPEND — do NOT revoke or replace existing previews.
    setFiles(prev => [...prev, ...accepted]);
    setPreviews(prev => [...prev, ...urls]);
    setEvidenceError(overflow ? t('report.evidence_max_reached') : '');
  };

  // Remove a specific evidence photo
  const removeEvidence = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setEvidenceError(''); // freeing a slot clears the overflow notice
  };

  // Upload evidence photos and get URLs
  const uploadEvidence = async () => {
    if (!files.length) return [];
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('ad_id', adId);
        formData.append('type', 'report_evidence');
        
        const res = await fetch(`${API_URL}/api/upload/evidence`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
        });
        
        if (!res.ok) {
          throw new Error('Upload failed');
        }
        
        const data = await res.json();
        if (data.url) {
          uploadedUrls.push(data.url);
        }
      }
      
      return uploadedUrls;
    } finally {
      setUploading(false);
    }
  };

  // Submit the final report
  const submitReport = async () => {
    if (!selected || !acknowledged || submitting) return;
    
    setSubmitting(true);
    
    try {
      // Upload evidence first
      let evidenceUrls = [];
      if (files.length > 0) {
        evidenceUrls = await uploadEvidence();
      }
      
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_URL}/api/ads/${adId}/report`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ 
          reason: selected, 
          details: details?.trim() || '',
          evidence_urls: evidenceUrls,
          acknowledged: true,
        }),
      });

      const data = await res.json();
      
      if (res.status === 409) {
        // Already reported
        toast.info(t('report.already_reported'));
        setStep('success');
        return;
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to submit report');
      }

      // Success - show success step
      setStep('success');
      toast.success(t('report.thanks'));
      onReported?.(data);
    } catch (err) {
      console.error('Report error:', err);
      toast.error(t('ras.anErrorOccurredPleaseTry'));
    } finally {
      setSubmitting(false);
    }
  };

  // Check if can continue to confirm step
  const canContinue = selected && acknowledged && !uploading && !submitting;

  // Get display name for selected reason
  const getReasonLabel = () => {
    if (!selected) return '';
    return t(`report.reason_${selected}`);
  };

  if (!mounted) return null;

  return (
    <div
      className={`ws-sheet-root ${open ? 'open' : 'closed'}`}
      dir={dir}
      aria-hidden={!open}
      data-testid="report-sheet-overlay"
    >
      {/* Backdrop */}
      <div className="ws-sheet-backdrop" onClick={resetAll} />
      
      {/* Sheet */}
      <div
        className="ws-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('report.title')}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid="report-sheet"
      >
        {/* Handle bar */}
        <div className="ws-sheet-handle" />
        
        {/* Header */}
        <div className="ws-sheet-header">
          <div className="ws-sheet-headings">
            <div className="ws-sheet-title">
              {step === 'confirm' ? t('report.confirm_title') : 
               step === 'success' ? t('report.success_title') : 
               t('report.title')}
            </div>
            {step === 'form' && (
              <div className="ws-sheet-subtitle">{t('report.subtitle')}</div>
            )}
          </div>
          <button 
            className="ws-sheet-x" 
            onClick={resetAll} 
            aria-label="Close"
            data-testid="report-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="ws-sheet-body">
          
          {/* ========== FORM STEP ========== */}
          {step === 'form' && (
            <>
              {/* Availability Section */}
              <div className="ws-section-label">{t('report.section_availability')}</div>
              <div className="ws-reason-grid">
                {availabilityReasons.map(r => (
                  <button
                    key={r.key}
                    type="button"
                    className={`ws-reason yellow ${selected === r.key ? 'active' : ''}`}
                    onClick={() => setSelected(r.key)}
                    data-testid={`reason-${r.key}`}
                  >
                    {t(r.i18nKey)}
                  </button>
                ))}
              </div>

              <div style={{ height: '14px' }} />

              {/* Policy Section */}
              <div className="ws-section-label">{t('report.section_policy')}</div>
              <div className="ws-reason-grid">
                {policyReasons.map(r => (
                  <button
                    key={r.key}
                    type="button"
                    className={`ws-reason red ${selected === r.key ? 'active' : ''}`}
                    onClick={() => setSelected(r.key)}
                    data-testid={`reason-${r.key}`}
                  >
                    {t(r.i18nKey)}
                  </button>
                ))}
              </div>

              <div style={{ height: '16px' }} />

              {/* Optional Details */}
              <label className="ws-label">{t('report.details_label')}</label>
              <textarea
                className="ws-textarea"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={t('report.details_ph')}
                rows={3}
                maxLength={500}
                data-testid="report-details"
              />

              {/* Evidence Photo Upload */}
              <div className="ws-evidence" data-testid="evidence-section">
                <div className="ws-evidence-head">
                  <div className="ws-evidence-title">{t('report.evidence_title')}</div>
                  <div className="ws-evidence-sub">{t('report.evidence_sub')}</div>
                </div>

                <div className="ws-evidence-row">
                  <label
                    className={`ws-evidence-btn${files.length >= MAX_EVIDENCE_FILES ? ' is-disabled' : ''}`}
                    data-testid="add-evidence-btn"
                    aria-disabled={files.length >= MAX_EVIDENCE_FILES}
                  >
                    <Camera size={18} />
                    <span>{t('report.add_photo')}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePickEvidence}
                      disabled={files.length >= MAX_EVIDENCE_FILES}
                      style={{ display: 'none' }}
                      data-testid="evidence-file-input"
                    />
                  </label>

                  {previews.length > 0 && (
                    <div className="ws-evidence-thumbs" data-testid="evidence-thumbs">
                      {previews.map((src, idx) => (
                        <div key={src} className="ws-thumb-wrapper" data-testid={`evidence-thumb-${idx}`}>
                          <img src={src} className="ws-thumb" alt={`Evidence ${idx + 1}`} />
                          <button
                            className="ws-thumb-remove"
                            onClick={() => removeEvidence(idx)}
                            type="button"
                            aria-label={t('report.remove_photo') || 'Remove'}
                            data-testid={`evidence-thumb-remove-${idx}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {evidenceError ? (
                  <div className="ws-evidence-error" role="alert" data-testid="evidence-error">
                    {evidenceError}
                  </div>
                ) : null}

                <div className="ws-evidence-hint">{t('report.evidence_limit')}</div>
              </div>

              {/* Accountability Acknowledgment (Required) */}
              <div className="ws-ack" data-testid="ack-section">
                <label className="ws-ack-row">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="ws-ack-checkbox"
                    data-testid="ack-checkbox"
                  />
                  <span className="ws-ack-text">{t('report.ack_text')}</span>
                </label>
              </div>
            </>
          )}

          {/* ========== CONFIRM STEP ========== */}
          {step === 'confirm' && (
            <>
              <div className="ws-confirm-card" data-testid="confirm-card">
                <div className="ws-confirm-row">
                  <span className="ws-confirm-k">{t('report.confirm_reason')}</span>
                  <span className="ws-confirm-v">{getReasonLabel()}</span>
                </div>
                
                {details?.trim() && (
                  <div className="ws-confirm-row">
                    <span className="ws-confirm-k">{t('report.confirm_details')}</span>
                    <span className="ws-confirm-v ws-confirm-details">{details.trim()}</span>
                  </div>
                )}

                {previews.length > 0 && (
                  <div className="ws-confirm-row">
                    <span className="ws-confirm-k">{t('report.confirm_evidence')}</span>
                    <span className="ws-confirm-v">
                      <div className="ws-evidence-thumbs">
                        {previews.map((src, idx) => (
                          <img key={idx} src={src} className="ws-thumb" alt={`Evidence ${idx + 1}`} />
                        ))}
                      </div>
                    </span>
                  </div>
                )}
              </div>

              {/* Warning Banner */}
              <div className="ws-warning" data-testid="false-warning">
                <AlertTriangle size={18} className="ws-warning-icon" />
                <span>{t('report.false_warning')}</span>
              </div>
            </>
          )}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && (
            <div className="ws-success" data-testid="success-message">
              <CheckCircle size={48} className="ws-success-icon" />
              <div className="ws-success-title">{t('report.success_title')}</div>
              <div className="ws-success-sub">{t('report.thanks')}</div>
            </div>
          )}
        </div>

        {/* Footer - Sticky with safe area */}
        <div className="ws-sheet-footer">
          {step === 'form' && (
            <>
              <button 
                className="ws-btn-secondary" 
                onClick={resetAll}
                disabled={submitting || uploading}
                data-testid="report-cancel-btn"
              >
                {t('report.cancel')}
              </button>
              <button
                className="ws-btn-primary"
                onClick={() => setStep('confirm')}
                disabled={!canContinue}
                data-testid="report-continue-btn"
              >
                {t('report.continue')}
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button 
                className="ws-btn-secondary" 
                onClick={() => setStep('form')}
                disabled={submitting || uploading}
                data-testid="report-back-btn"
              >
                <ChevronLeft size={16} />
                {t('report.back')}
              </button>
              <button
                className="ws-btn-primary"
                onClick={submitReport}
                disabled={submitting || uploading}
                data-testid="report-send-btn"
              >
                {uploading ? t('report.uploading') : 
                 submitting ? t('report.sending') : 
                 t('report.send')}
              </button>
            </>
          )}

          {step === 'success' && (
            <button
              className="ws-btn-primary ws-btn-full"
              onClick={resetAll}
              data-testid="report-done-btn"
            >
              {t('report.done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
